import React, { useEffect, useMemo, useState } from 'react';
import './relatorios.css';
import Sidebar from '../../components/Sidebar';
import Header from '../../components/header';
import Skeleton from '../../components/Skeleton';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChartColumn } from '@fortawesome/free-solid-svg-icons';
import { useUsers } from '../../context/usersContext';
import { useUnits } from '../../context/unitsContext';
import api from '../../services/api'; // os relatórios são servidos pelo mesmo domínio da API

// Unidades no formato do select (id + nome), em ordem alfabética.
const normalizeUnitOptions = (units) => units
  .map((item) => {
    const id = item?.id;
    if (id === null || id === undefined || id === '') return null;
    return { id, label: item?.name || item?.acronym || `Unidade ${id}` };
  })
  .filter(Boolean)
  .sort((a, b) => String(a.label).localeCompare(String(b.label), 'pt-BR'));

const Relatorios = () => {
  const { effectiveUnitId, isGlobalUnitUser } = useUsers();
  const { summaryListUnit, listUnits } = useUnits();
  const [unitOptions, setUnitOptions] = useState([]);
  const [isLoadingUnits, setIsLoadingUnits] = useState(false);
  // Usuário sem unidade escolhe aqui de qual unidade quer ver os relatórios (vale só nesta tela).
  const [selectedUnitId, setSelectedUnitId] = useState(() => (
    effectiveUnitId !== null && effectiveUnitId !== undefined ? String(effectiveUnitId) : ''
  ));

  useEffect(() => {
    if (!isGlobalUnitUser) return undefined;

    let isMounted = true;

    const loadUnits = async () => {
      setIsLoadingUnits(true);
      try {
        const summaryResponse = await summaryListUnit();
        let units = Array.isArray(summaryResponse)
          ? summaryResponse
          : summaryResponse?.message || summaryResponse?.results || [];

        if (!units.length) {
          const listResponse = await listUnits();
          units = Array.isArray(listResponse)
            ? listResponse
            : listResponse?.results || listResponse?.message || [];
        }

        if (isMounted) setUnitOptions(normalizeUnitOptions(units));
      } catch (error) {
        console.error('Erro ao listar unidades para relatório:', error);
        if (isMounted) setUnitOptions([]);
      } finally {
        if (isMounted) setIsLoadingUnits(false);
      }
    };

    loadUnits();

    return () => {
      isMounted = false;
    };
  }, [isGlobalUnitUser, summaryListUnit, listUnits]);

  // Usuário com unidade usa a dele (ou a ativa no topo da tela); sem unidade, a escolhida no select.
  const reportUnitId = isGlobalUnitUser ? selectedUnitId : effectiveUnitId;
  const apiBaseUrl = (api.defaults.baseURL || '').replace(/\/+$/, '');

  const reportsUrl = useMemo(() => {
    if (!apiBaseUrl) return '';
    if (reportUnitId === null || reportUnitId === undefined || String(reportUnitId) === '') return '';
    return `${apiBaseUrl}/internals/internal/reports/${reportUnitId}/`;
  }, [apiBaseUrl, reportUnitId]);

  // URL cujo iframe já terminou de carregar; enquanto for diferente da atual, mostra o skeleton.
  const [loadedReportsUrl, setLoadedReportsUrl] = useState('');
  const isReportLoading = Boolean(reportsUrl) && loadedReportsUrl !== reportsUrl;

  useEffect(() => {
    setLoadedReportsUrl('');
  }, [reportsUrl]);

  const emptyMessage = !apiBaseUrl
    ? 'URL de relatório não configurada.'
    : isLoadingUnits
      ? 'Carregando unidades...'
      : 'Selecione uma unidade para ver os relatórios.';

  return (
    <div>
      <Sidebar />
      <Header />
      <div className="container container-relatorios">
        {isGlobalUnitUser && (
          <div className="relatorios-filter">
            <label htmlFor="report-unit-select">Unidade</label>
            <select
              id="report-unit-select"
              value={selectedUnitId}
              onChange={(event) => setSelectedUnitId(event.target.value)}
              disabled={isLoadingUnits || unitOptions.length === 0}
            >
              <option value="">
                {isLoadingUnits ? 'Carregando unidades...' : 'Selecione a unidade'}
              </option>
              {unitOptions.map((unit) => (
                <option key={unit.id} value={unit.id}>
                  {unit.label}
                </option>
              ))}
            </select>
          </div>
        )}

        <div className="relatorios-frame-wrap">
          {reportsUrl ? (
            <>
              {isReportLoading && (
                <div className="relatorios-skeleton">
                  <Skeleton
                    type="stack"
                    lines={1}
                    text="Carregando relatórios..."
                    className="relatorios-skeleton-title"
                    prefix="reports-title"
                  />
                  <div className="relatorios-skeleton-cards">
                    {Array.from({ length: 4 }).map((_, index) => (
                      <Skeleton
                        key={`reports-card-${index}`}
                        className="relatorios-skeleton-card"
                      />
                    ))}
                  </div>
                  <Skeleton className="relatorios-skeleton-chart" />
                  <Skeleton type="stack" lines={4} prefix="reports-rows" />
                </div>
              )}
              <iframe
                key={reportsUrl}
                title="Relatórios SIAPEN"
                src={reportsUrl}
                className="relatorios-frame"
                onLoad={() => setLoadedReportsUrl(reportsUrl)}
              />
            </>
          ) : (
            <div className="relatorios-empty">
              <div className="relatorios-empty-icon">
                <FontAwesomeIcon icon={faChartColumn} />
              </div>
              <h3>Relatórios</h3>
              <p>{emptyMessage}</p>
              {apiBaseUrl && isGlobalUnitUser && !isLoadingUnits && (
                <span>Use o campo "Unidade" acima para escolher de qual unidade deseja visualizar os dados.</span>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Relatorios;
